import { apiRequest, buildQueryString, API_CONFIG, ApiResponse } from '../config/api';
import { DailySummaryData } from '../components/DailyNews/DailySummary';
import { Article } from '../config/api';

export interface DailySummaryFilters {
  page?: number;
  limit?: number;
  featured?: boolean;
  year?: number;
  month?: number;
}

export interface DailySummaryWithArticles {
  dailySummary: DailySummaryData;
  articles: Article[];
}

export const dailySummaryService = {
  // Get all daily summaries with pagination and filtering
  getDailySummaries: async (filters: DailySummaryFilters = {}): Promise<{
    data: DailySummaryData[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  }> => {
    const queryString = buildQueryString(filters);
    return apiRequest<{
      data: DailySummaryData[];
      pagination: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        hasNextPage: boolean;
        hasPrevPage: boolean;
      };
    }>(`${API_CONFIG.ENDPOINTS.DAILY_SUMMARIES || '/api/daily-summaries'}${queryString}`);
  },

  // Get single daily summary with articles by date
  getDailySummaryByDate: async (date: string): Promise<DailySummaryWithArticles> => {
    const response = await apiRequest<ApiResponse<DailySummaryWithArticles>>(
      `${API_CONFIG.ENDPOINTS.DAILY_SUMMARIES || '/api/daily-summaries'}/${date}`
    );
    if (!response.data) {
      throw new Error('Daily summary not found');
    }
    return response.data;
  },

  // Get daily summary by ID (with articles)
  getDailySummaryById: async (id: number): Promise<DailySummaryWithArticles> => {
    const response = await apiRequest<ApiResponse<DailySummaryWithArticles>>(
      `${API_CONFIG.ENDPOINTS.DAILY_SUMMARIES || '/api/daily-summaries'}/id/${id}`
    );
    if (!response.data) {
      throw new Error('Daily summary not found');
    }
    return response.data;
  },

  // Get recent daily summaries
  getRecentDailySummaries: async (limit: number = 5): Promise<DailySummaryData[]> => {
    const response = await apiRequest<{ data: DailySummaryData[] }>(
      `${API_CONFIG.ENDPOINTS.DAILY_SUMMARIES || '/api/daily-summaries'}/recent/${limit}`
    );
    return response.data;
  },

  // Get available years
  getAvailableYears: async (): Promise<number[]> => {
    const response = await apiRequest<{ data: number[] }>(
      `${API_CONFIG.ENDPOINTS.DAILY_SUMMARIES || '/api/daily-summaries'}/meta/years`
    );
    return response.data;
  },

  // Get available months for a year
  getAvailableMonths: async (year: number): Promise<{ month: number; count: number }[]> => {
    const response = await apiRequest<{ data: { month: number; count: number }[] }>(
      `${API_CONFIG.ENDPOINTS.DAILY_SUMMARIES || '/api/daily-summaries'}/meta/years/${year}/months`
    );
    return response.data;
  },

  // Create new daily summary (admin only)
  createDailySummary: async (summaryData: {
    date: string;
    title: string;
    summary: string;
    key_themes?: string[];
    featured?: boolean;
  }): Promise<{ message: string; dailySummary: DailySummaryData }> => {
    return apiRequest<{ message: string; dailySummary: DailySummaryData }>(
      API_CONFIG.ENDPOINTS.DAILY_SUMMARIES || '/api/daily-summaries',
      {
        method: 'POST',
        body: JSON.stringify(summaryData),
      }
    );
  },

  // Update daily summary (admin only)
  updateDailySummary: async (
    id: number,
    summaryData: {
      title?: string;
      summary?: string;
      key_themes?: string[];
      featured?: boolean;
    }
  ): Promise<{ message: string; dailySummary: DailySummaryData }> => {
    return apiRequest<{ message: string; dailySummary: DailySummaryData }>(
      `${API_CONFIG.ENDPOINTS.DAILY_SUMMARIES || '/api/daily-summaries'}/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(summaryData),
      }
    );
  },

  // Delete daily summary (admin only)
  deleteDailySummary: async (id: number): Promise<{ message: string }> => {
    return apiRequest<{ message: string }>(
      `${API_CONFIG.ENDPOINTS.DAILY_SUMMARIES || '/api/daily-summaries'}/${id}`,
      {
        method: 'DELETE',
      }
    );
  },

  // Helper function to format date for API calls
  formatDateForApi: (date: Date): string => {
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  },

  // Helper function to get today's date string
  getTodayDateString: (): string => {
    return dailySummaryService.formatDateForApi(new Date());
  },

  // Helper function to get date string for days ago
  getDateStringDaysAgo: (daysAgo: number): string => {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return dailySummaryService.formatDateForApi(date);
  },

  // Get daily summaries for a specific week
  getDailySummariesByWeek: async (weeklyId: string): Promise<DailySummaryData[]> => {
    const response = await apiRequest<{ data: DailySummaryData[] }>(
      `${API_CONFIG.ENDPOINTS.DAILY_SUMMARIES || '/api/daily-summaries'}/week/${weeklyId}`
    );
    return response.data;
  },
};
